import { scrollToFirstFormError } from "@insite/client-framework/Common/CustomerSelectors";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import setAddressBookOverlayIsOpen from "@insite/client-framework/Store/Components/AddressBookOverlay/Handlers/SetAddressBookOverlayIsOpen";
import setStorePickupOverlayIsOpen from "@insite/client-framework/Store/Components/StorePickupOverlay/Handlers/SetStorePickupOverlayIsOpen";
import { getCurrentUserIsGuest, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import loadShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTos";
import { getShipTosDataView, getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import saveShipping from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SaveShipping";
import setIsBillingAddressTheSameAsShipping from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetIsBillingAddressTheSameAsShipping";
import setPaymentDetailsState from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetPaymentDetailsState";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import { ShipToSelectorStyles } from "@insite/content-library/Components/ShipToSelector";
import { OnePageCheckoutFormContext } from "@insite/content-library/Pages/OnePageCheckoutPage";
import { BillingAddressFormStyles } from "@insite/content-library/Widgets/OnePageCheckout/BillingAddressForm";
import { BillToSelectorModalStyles } from "@insite/content-library/Widgets/OnePageCheckout/BillToSelectorModal";
import ShipToSelectorModal, {
    ShipToSelectorModalStyles,
} from "@insite/content-library/Widgets/OnePageCheckout/ShipToSelectorModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import ShippingAddressForm, {
    ShippingAddressFormStyles,
} from "@tggstore/Overrides/Widgets/OnePageCheckout/ShippingAddressForm";
import setUseBillingAddress from "@tggstore/Store/Handlers/Pages/OnePageCheckout/SetUseBillingAddress";
import setUseOneTimeAddress from "@tggstore/Store/Handlers/Pages/OnePageCheckout/SetUseOneTimeAddress";
import { CustomState } from "@tggstore/Store/Reducers";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: CustomState) => {
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const { value: cart } = cartState;
    const shipTosDataView = cart
        ? getShipTosDataView(state, { billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] })
        : undefined;
    let newAddress;
    let oneTimeAddress;
    let isShipToSameAsBillTo = false;
    let canUseBillToAsShipTo = false;
    if (cart && cart.cartLines && shipTosDataView && shipTosDataView.value) {
        const shipTos = shipTosDataView.value;
        newAddress = shipTos!.find(s => s.isNew && !s.oneTimeAddress);
        oneTimeAddress = shipTos!.find(s => s.oneTimeAddress);
        isShipToSameAsBillTo = cart.billToId === cart.shipToId;
        canUseBillToAsShipTo = shipTos!.some(shipTo => shipTo.id === cart.billToId);
    }

    // Verndale customization ticket: TGGOPT-2019
    const shipToAddressFields = getAddressFieldsDataView(state).value?.shipToAddressFields;
    const shipToAddressFieldDisplayCollection = shipToAddressFields as AddressFieldDisplayCollectionModel;

    const billToAddressFields = getAddressFieldsDataView(state).value?.billToAddressFields;
    const billToAddressFieldDisplayCollection = billToAddressFields as AddressFieldDisplayCollectionModel;

    const shipToState = getShipToState(state, cart?.shipToId);
    const address = shipToState.value;
    // End of Verndale customization ticket: TGGOPT-2019

    return {
        isShippingAddressUpdateRequired: state.pages.onePageCheckout.isShippingAddressUpdateRequired,
        isBillingAddressUpdateRequired: state.pages.onePageCheckout.isBillingAddressUpdateRequired,
        isBillingAddressTheSameAsShipping: state.pages.onePageCheckout.isBillingAddressTheSameAsShipping,
        isSavingShipping: state.pages.onePageCheckout.isSavingShipping,
        cart,
        shipToState,
        billToState: getBillToState(state, cart?.billToId),
        currentUserIsGuest: getCurrentUserIsGuest(state),
        countries: getCurrentCountries(state),
        shipTosDataView: cart
            ? getShipTosDataView(state, { billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] })
            : undefined,
        shipToAddressFields,
        billToAddressFields: getAddressFieldsDataView(state).value?.billToAddressFields,
        fulfillmentMethod: getSession(state).fulfillmentMethod,
        pickUpWarehouse: getSession(state).pickUpWarehouse,
        newAddress,
        // Verndale customization ticket: TGGOPT-2019
        shippingAddressFormState: state.customOnePageCheckout.shippingAddressFormState,
        currentBillToId: cart?.billToId,
        useOneTimeAddress: state.customOnePageCheckout.useOneTimeAddress,
        useBillingAddress: state.customOnePageCheckout.useBillingAddress,
        oneTimeAddress,
        showUseBillingAddress: canUseBillToAsShipTo,
        isUseBillingAddressDisabled: isShipToSameAsBillTo,
        shipToAddressFieldDisplayCollection,
        billToAddressFieldDisplayCollection,
        address,
        cartPageLink: getPageLinkByPageType(state, "CartPage")?.url,
        // End of Verndale customization ticket: TGGOPT-2019
    };
};

const mapDispatchToProps = {
    loadShipTos,
    loadShipTo,
    loadBillTo,
    setIsBillingAddressTheSameAsShipping,
    setAddressBookOverlayIsOpen,
    setStorePickupOverlayIsOpen,
    saveShipping,
    setPaymentDetailsState,
    // Verndale customization ticket: TGGOPT-2019
    setUseOneTimeAddress,
    setUseBillingAddress,
};

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasHistory;

// Verndale customization ticket: TGGOPT-2019
interface VerndaleCustomizationStyles {
    oneTimeAddressGridItem?: GridItemProps;
    addressDisplayAndFormGridItem?: GridItemProps;
    oneTimeAddressCheckboxGroup?: FieldSetGroupPresentationProps<CheckboxGroupComponentProps>;
    oneTimeAddressCheckbox?: CheckboxPresentationProps;
    addressDisplay?: AddressInfoDisplayStyles;
    selectSavedAddressLink?: LinkPresentationProps;
    shipToSelector?: ShipToSelectorStyles;
    addressBookModal?: ModalPresentationProps;
    addressBookModalButtonsWrapper?: InjectableCss;
    addressBookModalCancelButton?: ButtonPresentationProps;
    addressBookModalShipToButtonsWrapper?: InjectableCss;
    addressBookModalShipToButton?: ButtonPresentationProps;
    addressBookModalBillToButton?: ButtonPresentationProps;
    addressBookModalSaveShipToButton?: ButtonPresentationProps;
    addressBookModalCancelShipToButton?: ButtonPresentationProps;
    addressBookModalShipToBillToButtonsWrapper?: InjectableCss;
}
// End of Verndale customization ticket: TGGOPT-2019

export interface OnePageCheckoutDeliveryAddressSectionStyles extends VerndaleCustomizationStyles {
    loadingWrapper?: InjectableCss;
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    titleEditGridItem?: GridItemProps;
    titleEditLink?: LinkPresentationProps;
    pickUpGridItem?: GridItemProps;
    headingGridItem?: GridItemProps;
    heading?: TypographyPresentationProps;
    editLink?: LinkPresentationProps;
    shipToSelectorModal?: ShipToSelectorModalStyles;
    billToSelectorModal?: BillToSelectorModalStyles;
    shipToGridItem?: GridItemProps;
    shippingAddressForm?: ShippingAddressFormStyles;
    addressInfoDisplay?: AddressInfoDisplayStyles;
    useShippingAddressGridItem?: GridItemProps;
    useShippingAddressCheckbox?: CheckboxPresentationProps;
    billToGridItem?: GridItemProps;
    billingAddressForm?: BillingAddressFormStyles;
    saveButtonGridItem?: GridItemProps;
    saveButton?: ButtonPresentationProps;
}

export const onePageCheckoutDeliveryAddressSectionStyles: OnePageCheckoutDeliveryAddressSectionStyles = {
    loadingWrapper: {
        css: css`
            display: flex;
            height: 450px;
            justify-content: center;
            align-items: center;
        `,
    },
    container: { gap: 10 },
    titleGridItem: { width: 10 },
    titleText: {
        variant: "h5",
        css: css`
            margin: 0;
        `,
    },
    titleEditGridItem: {
        width: 2,
        css: css`
            justify-content: end;
            align-items: center;
        `,
    },
    pickUpGridItem: {
        width: 12,
        css: css`
            padding-bottom: 24px;
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            justify-content: space-between;
            padding: 15px 5px 5px 5px;
        `,
    },
    heading: {
        weight: 700,
        css: css`
            font-size: 24px;
        `,
    },
    shipToGridItem: { width: 12 },
    useShippingAddressGridItem: {
        width: 12,
        css: css`
            padding: 20px 5px;
        `,
    },
    billToGridItem: { width: 12 },
    saveButtonGridItem: { width: 12 },
    saveButton: {
        css: css`
            margin-top: 10px;
        `,
    },

    // Verndale customization ticket: TGGOPT-2019
    oneTimeAddressGridItem: { width: 12 },
    addressDisplayAndFormGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    selectSavedAddressLink: {
        css: css`
            margin-left: 20px;
        `,
    },
    addressBookModal: { sizeVariant: "medium" },
    addressBookModalButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 1rem;
        `,
    },
    addressBookModalCancelButton: { variant: "secondary" },
    addressBookModalShipToButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-top: 1rem;
            gap: 1rem;
        `,
    },
    addressBookModalBillToButton: {
        variant: "secondary",
    },
    addressBookModalShipToButton: {
        variant: "secondary",
    },
    addressBookModalSaveShipToButton: {
        variant: "primary",
    },
    addressBookModalCancelShipToButton: {
        variant: "secondary",
    },
    addressBookModalShipToBillToButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-top: 1rem;
            gap: 1rem;
            width: 100%;
        `,
    },
};

const styles = onePageCheckoutDeliveryAddressSectionStyles;
const StyledForm = getStyledWrapper("form");

const OnePageCheckoutDeliveryAddressSection = ({
    isShippingAddressUpdateRequired,
    isBillingAddressUpdateRequired,
    isSavingShipping,
    cart,
    shipToState,
    billToState,
    currentUserIsGuest,
    countries,
    shipTosDataView,
    fulfillmentMethod,
    newAddress,
    theme,
    loadShipTos,
    loadShipTo,
    loadBillTo,
    setAddressBookOverlayIsOpen,
    // Verndale customization ticket: TGGOPT-2019
    shippingAddressFormState,
    currentBillToId,
    setUseOneTimeAddress,
    setUseBillingAddress,
    oneTimeAddress,
    showUseBillingAddress,
    useOneTimeAddress,
    useBillingAddress,
    isUseBillingAddressDisabled,
    shipToAddressFieldDisplayCollection,
    address,
    saveShipping,
    setIsBillingAddressTheSameAsShipping,
}: // End of Verndale customization ticket: TGGOPT-2019
Props) => {
    const context = useContext(OnePageCheckoutFormContext);
    const [isEditing, setIsEditing] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const [isLoading, setIsLoading] = useState(false);
    const [isShipToSelectorModalOpen, setIsShipToSelectorModalOpen] = useState(false);
    const [isContinueToPayment, setIsContinueToPayment] = useState(false);

    useEffect(() => {
        if ((isShippingAddressUpdateRequired || isBillingAddressUpdateRequired) && shipToState.value) {
            setIsEditing(true);
        }
    }, [isShippingAddressUpdateRequired, isBillingAddressUpdateRequired, shipToState.value]);

    const shipTo = shipToState.value;
    const billTo = billToState.value;

    useEffect(() => {
        if (cart && shipTosDataView && !shipTosDataView.value && !shipTosDataView.isLoading) {
            loadShipTos({ billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] });
        }
    });

    useEffect(() => {
        if (!shipToState.value && !shipToState.isLoading && cart && cart.billToId && cart.shipToId) {
            loadShipTo({ billToId: cart.billToId, shipToId: cart.shipToId });
        }
    }, [shipToState]);

    useEffect(() => {
        if (!billToState.value && !billToState.isLoading && cart && cart.billToId) {
            loadBillTo({ billToId: cart.billToId });
        }
    }, [billToState]);

    if (!cart) {
        return (
            <StyledWrapper {...styles.loadingWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (!countries || !billTo || (!shipTo && !newAddress)) {
        return null;
    }

    const shipToSelectorModalCloseHandler = () => {
        setIsShipToSelectorModalOpen(false);
    };

    const addressTypeChangeHandler: RadioGroupProps["onChangeHandler"] = event => {
        setIsLoading(true);
        if (event.target.value === "onetime") {
            setUseOneTimeAddress({
                useOneTimeAddress: true,
            });
        } else if (event.target.value === "billing") {
            setUseBillingAddress({
                useBillingAddress: true,
            });
        }
        setIsLoading(false);
    };

    const oneTimeAddressChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setIsLoading(true);
        setUseOneTimeAddress({
            useOneTimeAddress: value,
        });
        setIsLoading(false);
    };

    const useBillingAddressChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setIsLoading(true);
        setUseBillingAddress({
            useBillingAddress: value,
        });
        setIsLoading(false);
    };

    const selectAddressClickHandler = () => {
        theme.addressBook?.useOverlay
            ? setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: false })
            : setIsShipToSelectorModalOpen(true);
    };

    const useBillToHandler = () => {
        setIsLoading(true);

        setTimeout(() => {
            setUseOneTimeAddress({
                useOneTimeAddress: true,
                billToAddress: billTo,
            });
            toasterContext.addToast({
                messageType: "success",
                body: translate("BillTo address has been selected."),
            });
            setIsLoading(false);
        }, 500);
    };

    const cancelHandler = () => {
        setUseBillingAddress({
            useBillingAddress: true,
        });
    };

    const useShipToHandler = () => {
        setIsLoading(true);

        setTimeout(() => {
            setUseOneTimeAddress({
                useOneTimeAddress: true,
                address: shipToState.value,
            });
            toasterContext.addToast({
                messageType: "success",
                body: translate("ShipTo address has been selected."),
            });
            setIsLoading(false);
        }, 500);
    };

    const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        context.formSubmitAttempted = true;

        const validators: (() => Promise<boolean>)[] = [];
        Object.keys(context.validators).forEach(key => {
            const validator = context.validators[key];
            if (validator) {
                validators.push(validator);
            }
        });
        const results = await Promise.all(validators.map(v => v()));

        if (results.every(o => o)) {
            setIsBillingAddressTheSameAsShipping({ isBillingAddressTheSameAsShipping: false });

            saveShipping({
                onSuccess: () => {
                    setIsEditing(false);
                    setIsLoading(false);
                    setIsContinueToPayment(true);
                },
            });
        } else {
            scrollToFirstFormError();
        }
    };

    return (
        <LoadingOverlay loading={isSavingShipping}>
            <GridContainer {...styles.container} data-test-selector="shippingAndBillingSection">
                {!isEditing && (
                    <>
                        {fulfillmentMethod === FulfillmentMethod.Ship && (shipTo || newAddress) && (
                            <>
                                <GridItem {...styles.headingGridItem}>
                                    <Typography {...styles.heading} as="h2">
                                        {translate("Delivery Address")}
                                    </Typography>
                                    {!currentUserIsGuest && (
                                        <Link
                                            {...styles.selectSavedAddressLink}
                                            type="button"
                                            onClick={selectAddressClickHandler}
                                            data-test-selector="checkoutShipping_selectSavedAddress"
                                        >
                                            {translate("Select Saved Address")}
                                            <VisuallyHidden>{translate("For Shipping Address")}</VisuallyHidden>
                                        </Link>
                                    )}
                                    <ShipToSelectorModal
                                        isOpen={isShipToSelectorModalOpen}
                                        onModalClose={shipToSelectorModalCloseHandler}
                                        extendedStyles={styles.shipToSelectorModal}
                                    />
                                </GridItem>
                                {oneTimeAddress && showUseBillingAddress && !isContinueToPayment ? (
                                    <GridItem {...styles.oneTimeAddressGridItem}>
                                        <RadioGroup
                                            value={
                                                shippingAddressFormState
                                                    ? shippingAddressFormState.address.oneTimeAddress
                                                        ? "onetime"
                                                        : shippingAddressFormState.address.id === currentBillToId
                                                        ? "billing"
                                                        : ""
                                                    : "billing"
                                            }
                                            onChangeHandler={addressTypeChangeHandler}
                                        >
                                            <Radio
                                                value="onetime"
                                                data-test-selector="checkoutShipping_useOneTimeAddress"
                                            >
                                                {translate("Ship to One-Time Address")}
                                            </Radio>
                                            <Radio value="billing">{translate("Use Billing Address")}</Radio>
                                        </RadioGroup>
                                    </GridItem>
                                ) : (
                                    <>
                                        {oneTimeAddress && !isContinueToPayment && (
                                            <GridItem {...styles.oneTimeAddressGridItem}>
                                                <CheckboxGroup {...styles.oneTimeAddressCheckboxGroup}>
                                                    <Checkbox
                                                        {...styles.oneTimeAddressCheckbox}
                                                        onChange={oneTimeAddressChangeHandler}
                                                        checked={useOneTimeAddress}
                                                        disabled={currentUserIsGuest}
                                                    >
                                                        {translate("Ship to One-Time Address")}
                                                    </Checkbox>
                                                </CheckboxGroup>
                                            </GridItem>
                                        )}
                                        {showUseBillingAddress && !isContinueToPayment && (
                                            <GridItem {...styles.oneTimeAddressGridItem}>
                                                <CheckboxGroup {...styles.oneTimeAddressCheckboxGroup}>
                                                    <Checkbox
                                                        {...styles.oneTimeAddressCheckbox}
                                                        onChange={useBillingAddressChangeHandler}
                                                        checked={useBillingAddress || isUseBillingAddressDisabled}
                                                        disabled={isUseBillingAddressDisabled}
                                                    >
                                                        {translate("Use Billing Address")}
                                                    </Checkbox>
                                                </CheckboxGroup>
                                            </GridItem>
                                        )}
                                        {isContinueToPayment && (
                                            <>
                                            { translate("Using ")}
                                            {    shippingAddressFormState
                                                    ? shippingAddressFormState.address.oneTimeAddress
                                                        ? "onetime"
                                                        : shippingAddressFormState.address.id === currentBillToId
                                                        ? "billing"
                                                        : ""
                                                    : "billing"
                                            } {translate(" Address")}
                                            </>
                                        )}
                                    </>
                                )}
                                <GridItem {...styles.addressDisplayAndFormGridItem}>
                                    {(useOneTimeAddress || currentUserIsGuest || isShippingAddressUpdateRequired) &&
                                        !isContinueToPayment && (
                                            <>
                                                <StyledForm
                                                    style={{ width: "100%" }}
                                                    onSubmit={handleFormSubmit}
                                                    noValidate={true}
                                                    data-test-selector="onePageCheckoutDeliveryAddressSectionForm"
                                                    id="onePageCheckoutDeliveryAddressSectionForm"
                                                >
                                                    {isLoading && (
                                                        <StyledWrapper {...styles.loadingWrapper}>
                                                            <LoadingSpinner />
                                                        </StyledWrapper>
                                                    )}
                                                    {!isLoading && (
                                                        <ShippingAddressForm
                                                            countries={countries}
                                                            fieldDisplay={shipToAddressFieldDisplayCollection}
                                                        />
                                                    )}
                                                    <StyledWrapper
                                                        {...styles.addressBookModalShipToBillToButtonsWrapper}
                                                    >
                                                        <StyledWrapper {...styles.addressBookModalShipToButtonsWrapper}>
                                                            <Button
                                                                {...styles.addressBookModalSaveShipToButton}
                                                                type="submit"
                                                                data-test-selector="onePageCheckoutSaveShipToButton"
                                                                id="onePageCheckoutSaveShipToButton"
                                                                hidden={true}
                                                            >
                                                                {translate("Save")}
                                                            </Button>
                                                            {/* <Button
                                                                {...styles.addressBookModalCancelShipToButton}
                                                                onClick={cancelHandler}
                                                                data-test-selector="onePageCheckoutCancelShipToButton"
                                                                id="onePageCheckoutCancelShipToButton"
                                                                type="button"
                                                            >
                                                                {translate("Cancel")}
                                                            </Button> */}
                                                        </StyledWrapper>
                                                        <StyledWrapper {...styles.addressBookModalShipToButtonsWrapper}>
                                                            <Button
                                                                {...styles.addressBookModalShipToButton}
                                                                onClick={useShipToHandler}
                                                                data-test-selector="addressModal_useShipTo"
                                                                type="button"
                                                                disabled={isLoading}
                                                            >
                                                                {translate("Use ShipTo")}
                                                            </Button>
                                                            <Button
                                                                {...styles.addressBookModalBillToButton}
                                                                onClick={useBillToHandler}
                                                                data-test-selector="addressModal_useBillTo"
                                                                type="button"
                                                                disabled={isLoading}
                                                            >
                                                                {translate("Use BillTo")}
                                                            </Button>
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                </StyledForm>
                                            </>
                                        )}
                                    {!useOneTimeAddress &&
                                        !currentUserIsGuest &&
                                        !isShippingAddressUpdateRequired &&
                                        !isContinueToPayment &&
                                        address && (
                                            <AddressInfoDisplay
                                                {...address}
                                                state={address.state ? address.state.abbreviation : undefined}
                                                country={address.country ? address.country.abbreviation : undefined}
                                                extendedStyles={styles.addressDisplay}
                                            />
                                        )}
                                    {isContinueToPayment && address && (
                                        <AddressInfoDisplay
                                        {...address}
                                        state={address.state ? address.state.abbreviation : undefined}
                                        country={address.country ? address.country.abbreviation : undefined}
                                        extendedStyles={styles.addressDisplay}
                                    />
                                    )}
                                </GridItem>
                            </>
                        )}
                    </>
                )}
            </GridContainer>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(
        mapStateToProps,
        mapDispatchToProps,
    )(withHistory(withTheme(OnePageCheckoutDeliveryAddressSection))),
    definition: {
        displayName: "Delivery Address Section",
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
    },
};

export default widgetModule;
